import React, { useState } from 'react';
import {
  Button,
  Radio,
  RadioGroup,
  Input,
  Tag,
  makeStyles,
  shorthands,
} from '@fluentui/react-components';
import { BaseDialog } from './BaseDialog';

const useStyles = makeStyles({
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '24px',
  },
  tagSection: {
    display: 'flex',
    gap: '16px',
    marginTop: '16px',
  },
  tagColumn: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius('4px'),
  },
  tagColumnTitle: {
    fontWeight: '600',
    fontSize: '14px',
    marginBottom: '12px',
  },
  searchInput: {
    marginBottom: '12px',
  },
  tagList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  availableTag: {
    display: 'inline-flex',
    margin: '4px',
  },
  filterHint: {
    fontSize: '12px',
    color: '#666',
    marginTop: '8px',
  },
  selectedTagsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  createTagButton: {
    marginTop: '8px',
  },
});

interface EditTagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerCount: number;
  onSave: (data: EditTagsData) => void;
}

export interface EditTagsData {
  action: 'add' | 'replace' | 'remove' | 'removeAll';
  tags?: string[];
}

const availableTags = ['BESTCUSTOMER', 'MYTAG'];

export default function EditTagsModal({
  isOpen,
  onClose,
  customerCount,
  onSave,
}: EditTagsModalProps) {
  const styles = useStyles();
  const [selectedAction, setSelectedAction] = useState<'add' | 'replace' | 'remove' | 'removeAll'>('add');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState('');

  const filteredAvailableTags = availableTags.filter(tag =>
    tag.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleTagClick = (tag: string) => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleRemoveTag = (tag: string) => {
    setSelectedTags(selectedTags.filter(t => t !== tag));
  };

  const handleCreateTag = () => {
    if (newTagInput.trim() && !selectedTags.includes(newTagInput.trim())) {
      setSelectedTags([...selectedTags, newTagInput.trim()]);
      setNewTagInput('');
    }
  };

  const handleSave = () => {
    const data: EditTagsData = {
      action: selectedAction,
      tags: selectedAction !== 'removeAll' ? selectedTags : undefined,
    };
    onSave(data);
    handleCancel();
  };

  const handleCancel = () => {
    setSelectedAction('add');
    setSearchTerm('');
    setSelectedTags([]);
    setNewTagInput('');
    onClose();
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={handleCancel}
      title={`Edit tags of ${customerCount} customers`}
      maxWidth="560px"
      showCloseButton={true}
      contentPadding="24px"
      actions={
        <>
          <Button appearance="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={handleSave}>
            Add tags
          </Button>
        </>
      }
    >
      <div>
            <RadioGroup
              value={selectedAction}
              onChange={(e, data) => setSelectedAction(data.value as any)}
              className={styles.radioGroup}
            >
              <Radio value="add" label="I want to add the following tags:" />
              <Radio value="replace" label="I want to replace all tags with:" />
              <Radio value="remove" label="I want to remove the following tags:" />
              <Radio value="removeAll" label="I want to remove all tags." />
            </RadioGroup>

            {selectedAction !== 'removeAll' && (
              <div className={styles.tagSection}>
                <div className={styles.tagColumn}>
                  <div className={styles.tagColumnTitle}>Available tags</div>
                  <Input
                    className={styles.searchInput}
                    placeholder="Search tag"
                    value={searchTerm}
                    onChange={(e, data) => setSearchTerm(data.value)}
                  />
                  <div className={styles.tagList}>
                    {filteredAvailableTags.map(tag => (
                      <Tag
                        key={tag}
                        className={styles.availableTag}
                        onClick={() => handleTagClick(tag)}
                        style={{ cursor: 'pointer' }}
                      >
                        {tag}
                      </Tag>
                    ))}
                  </div>
                  <div className={styles.filterHint}>Use the filter to show all tags.</div>
                </div>

                <div className={styles.tagColumn}>
                  <div className={styles.tagColumnTitle}>Selected tags</div>
                  <div className={styles.selectedTagsContainer}>
                    <Input
                      placeholder="Tag"
                      value={newTagInput}
                      onChange={(e, data) => setNewTagInput(data.value)}
                      style={{ flex: 1 }}
                    />
                    <Button appearance="primary" onClick={handleCreateTag}>
                      Create tag
                    </Button>
                  </div>
                  <div className={styles.tagList} style={{ marginTop: '12px' }}>
                    {selectedTags.map(tag => (
                      <Tag
                        key={tag}
                        className={styles.availableTag}
                        dismissible
                        dismissIcon={{ onClick: () => handleRemoveTag(tag) }}
                      >
                        {tag}
                      </Tag>
                    ))}
                  </div>
                </div>
              </div>
            )}
      </div>
    </BaseDialog>
  );
}
